import { createContext, useContext, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import noTranslations from "../locales/no.json";
import enTranslations from "../locales/en.json";

const translations = {
  no: noTranslations,
  en: enTranslations,
};

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState("no");
  
  // Get nested translation value by dot notation key
  const t = (key) => {
    const keys = key.split(".");
    let value = translations[language];
    for (const k of keys) {
      if (value && typeof value === "object" && k in value) {
        value = value[k];
      } else {
        return key; // Return key if not found
      }
    }
    return typeof value === "string" ? value : key;
  };

  // Get array from translations (for testimonials, etc.)
  const tArray = (key) => {
    const keys = key.split(".");
    let value = translations[language];
    for (const k of keys) {
      if (value && typeof value === "object" && k in value) {
        value = value[k];
      } else {
        return [];
      }
    }
    return Array.isArray(value) ? value : [];
  };

  const setLanguage = (lang) => {
    setLanguageState(lang);
    localStorage.setItem("preferredLanguage", lang);
    // Update document lang attribute
    document.documentElement.lang = lang;
  };

  // Initialize language from URL or localStorage
  useEffect(() => {
    const storedLang = localStorage.getItem("preferredLanguage");
    if (storedLang && (storedLang === "no" || storedLang === "en")) {
      setLanguageState(storedLang);
      document.documentElement.lang = storedLang;
    }
  }, []);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, tArray }}>
      {children}
    </LanguageContext.Provider>
  );
};

// Hook to sync language with URL
export const useLanguageFromUrl = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { language, setLanguage } = useLanguage();

  useEffect(() => {
    const pathLang = location.pathname.split("/")[1];
    if (pathLang === "en" && language !== "en") {
      setLanguage("en");
    } else if (pathLang === "no" && language !== "no") {
      setLanguage("no");
    }
  }, [location.pathname, language, setLanguage]);

  const switchLanguage = (newLang) => {
    const currentPath = location.pathname;
    const pathParts = currentPath.split("/");
    
    // Replace the language prefix
    if (pathParts[1] === "no" || pathParts[1] === "en") {
      pathParts[1] = newLang;
    } else {
      pathParts.splice(1, 0, newLang);
    }
    
    const newPath = pathParts.join("/") || `/${newLang}`;
    setLanguage(newLang);
    navigate(newPath);
  };

  return { switchLanguage };
};
